import { ThreadTokenUsage, MessageTokenUsage, ModelOption, ModelPricing, OpenAITokenUsage } from '../interfaces/chat-component.interfaces';

export interface TokenCostBreakdown {
  inputCost: number;
  outputCost: number;
  totalCost: number;
  currency: string;
}

interface ResolvedModelPricing {
  input: number;
  output: number;
}
/**
 * Token Usage Tracking Utilities
 * 
 * These utilities help track and manage token usage for AI chat threads.
 * Use these functions when making API calls to update token usage data.
 */

/**
 * Converts OpenAI API token usage to our internal format
 */
export function convertOpenAITokenUsage(openaiUsage: OpenAITokenUsage, model: string): MessageTokenUsage {
  return {
    promptTokens: openaiUsage.prompt_tokens,
    completionTokens: openaiUsage.completion_tokens,
    totalTokens: openaiUsage.total_tokens,
    model: model
  };
}

/**
 * Updates thread token usage with new message usage data
 */
export function updateThreadTokenUsage(
  thread: any, 
  messageUsage: MessageTokenUsage
): ThreadTokenUsage {
  const existingUsage = thread.tokenUsage || {
    totalPromptTokens: 0,
    totalCompletionTokens: 0,
    totalTokens: 0,
    apiCallCount: 0,
    lastUpdated: new Date()
  };

  return {
    totalPromptTokens: existingUsage.totalPromptTokens + messageUsage.promptTokens,
    totalCompletionTokens: existingUsage.totalCompletionTokens + messageUsage.completionTokens,
    totalTokens: existingUsage.totalTokens + messageUsage.totalTokens,
    apiCallCount: existingUsage.apiCallCount + 1,
    lastUpdated: new Date()
  };
}

/**
 * Example: How to track token usage in your AI service
 * 
 * async makeAIRequest(prompt: string, thread: any) {
 *   try {
 *     const response = await this.aiApi.generateContent(prompt);
 *     
 *     // Extract token usage from API response
 *     if (response.usage) {
 *       const messageUsage = convertOpenAITokenUsage(response.usage, 'gpt-4');
 *       const updatedTokenUsage = updateThreadTokenUsage(thread, messageUsage);
 *       
 *       // Update the thread with new token usage
 *       await this.updateThreadTokenUsage(thread.id, updatedTokenUsage);
 *     }
 *     
 *     return response.text;
 *   } catch (error) {
 *     console.error('AI request failed:', error);
 *     throw error;
 *   }
 * }
 */

const OPENAI_MODEL_PRICING: Record<string, ModelPricing> = {
  'openai/gpt-5': {
    input: 1.25 / 1000000,
    output: 10.0 / 1000000,
    cachedInput: 0.125 / 1000000,
    displayName: 'GPT-5',
    category: 'gpt-5'
  },
  'openai/gpt-5-mini': {
    input: 0.25 / 1000000,
    output: 2.0 / 1000000,
    cachedInput: 0.025 / 1000000,
    displayName: 'GPT-5 Mini',
    category: 'gpt-5'
  },
  'openai/gpt-5-nano': {
    input: 0.05 / 1000000,
    output: 0.4 / 1000000,
    cachedInput: 0.005 / 1000000,
    displayName: 'GPT-5 Nano',
    category: 'gpt-5'
  },
  'openai/gpt-4o': {
    input: 2.5 / 1000000,
    output: 10.0 / 1000000,
    displayName: 'GPT-4o',
    category: 'gpt-4'
  },
  'openai/gpt-4': {
    input: 30.0 / 1000000,
    output: 60.0 / 1000000,
    displayName: 'GPT-4',
    category: 'gpt-4'
  },
  'openai/gpt-3.5-turbo': {
    input: 0.5 / 1000000,
    output: 1.5 / 1000000,
    displayName: 'GPT-3.5 Turbo',
    category: 'gpt-3.5'
  }
};

const LEGACY_MODEL_KEY_MAP: Record<string, string> = {
  'gpt-5': 'openai/gpt-5',
  'gpt-5-mini': 'openai/gpt-5-mini',
  'gpt-5-nano': 'openai/gpt-5-nano',
  'gpt-4o': 'openai/gpt-4o',
  'gpt-4': 'openai/gpt-4',
  'gpt-3.5-turbo': 'openai/gpt-3.5-turbo'
};

export const DEFAULT_MODEL_KEY = 'openai/gpt-5-mini';

/**
 * Model pricing configuration (per million tokens)
 * Includes both canonical OpenRouter keys and legacy aliases used by older threads.
 */
export const MODEL_PRICING: Record<string, ModelPricing> = {
  ...OPENAI_MODEL_PRICING,
  'gpt-5': OPENAI_MODEL_PRICING['openai/gpt-5'],
  'gpt-5-mini': OPENAI_MODEL_PRICING['openai/gpt-5-mini'],
  'gpt-5-nano': OPENAI_MODEL_PRICING['openai/gpt-5-nano'],
  'gpt-4o': OPENAI_MODEL_PRICING['openai/gpt-4o'],
  'gpt-4': OPENAI_MODEL_PRICING['openai/gpt-4'],
  'gpt-3.5-turbo': OPENAI_MODEL_PRICING['openai/gpt-3.5-turbo']
};

export function getCanonicalModelKey(modelKey?: string): string | undefined {
  if (!modelKey) {
    return undefined;
  }

  return LEGACY_MODEL_KEY_MAP[modelKey] || modelKey;
}

export function getModelPricing(modelKey?: string): ModelPricing | undefined {
  const canonicalKey = getCanonicalModelKey(modelKey);

  if (!canonicalKey) {
    return undefined;
  }

  if (MODEL_PRICING[canonicalKey]) {
    return MODEL_PRICING[canonicalKey];
  }

  return modelKey ? MODEL_PRICING[modelKey] : undefined;
}

function getCatalogModelOption(modelKey: string | undefined, availableModels: ModelOption[] = []): ModelOption | undefined {
  const canonicalKey = getCanonicalModelKey(modelKey);
  const candidateKeys = [modelKey, canonicalKey].filter((value): value is string => !!value);

  return availableModels.find(model =>
    candidateKeys.includes(model.key) ||
    candidateKeys.some(candidateKey => model.aliasKeys?.includes(candidateKey))
  );
}

function resolveModelPricing(modelKey?: string, availableModels: ModelOption[] = []): ResolvedModelPricing | undefined {
  const catalogModel = getCatalogModelOption(modelKey, availableModels);
  const promptPrice = catalogModel?.promptPrice ?? catalogModel?.input;
  const completionPrice = catalogModel?.completionPrice ?? catalogModel?.output;

  if (Number.isFinite(promptPrice) && Number.isFinite(completionPrice)) {
    return {
      input: Number(promptPrice),
      output: Number(completionPrice)
    };
  }

  const fallbackPricing = getModelPricing(modelKey);
  if (!fallbackPricing) {
    return undefined;
  }

  return {
    input: fallbackPricing.input,
    output: fallbackPricing.output
  };
}

export function formatUsd(value: number, currency: string = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: value < 0.01 ? 4 : 2,
    maximumFractionDigits: value < 0.01 ? 4 : 2
  }).format(value);
}

export function getMessageCostBreakdown(tokenUsage?: MessageTokenUsage | null, availableModels: ModelOption[] = []): TokenCostBreakdown | null {
  if (!tokenUsage) {
    return null;
  }

  const currency = tokenUsage.currency || 'USD';
  const inputCost = typeof tokenUsage.promptCost === 'number'
    ? tokenUsage.promptCost
    : typeof tokenUsage.promptTokenPrice === 'number'
      ? tokenUsage.promptTokens * tokenUsage.promptTokenPrice
      : undefined;
  const outputCost = typeof tokenUsage.completionCost === 'number'
    ? tokenUsage.completionCost
    : typeof tokenUsage.completionTokenPrice === 'number'
      ? tokenUsage.completionTokens * tokenUsage.completionTokenPrice
      : undefined;
  const totalCost = typeof tokenUsage.totalCost === 'number'
    ? tokenUsage.totalCost
    : inputCost !== undefined && outputCost !== undefined
      ? inputCost + outputCost
      : undefined;

  if (inputCost !== undefined && outputCost !== undefined && totalCost !== undefined) {
    return { inputCost, outputCost, totalCost, currency };
  }

  const pricing = resolveModelPricing(tokenUsage.model, availableModels);
  if (!pricing) {
    return null;
  }

  return {
    inputCost: tokenUsage.promptTokens * pricing.input,
    outputCost: tokenUsage.completionTokens * pricing.output,
    totalCost: (tokenUsage.promptTokens * pricing.input) + (tokenUsage.completionTokens * pricing.output),
    currency
  };
}

export function getThreadCostBreakdown(tokenUsage?: ThreadTokenUsage | null, modelKey?: string, availableModels: ModelOption[] = []): TokenCostBreakdown | null {
  if (!tokenUsage) {
    return null;
  }

  const currency = tokenUsage.currency || 'USD';
  const inputCost = typeof tokenUsage.totalPromptCost === 'number' ? tokenUsage.totalPromptCost : undefined;
  const outputCost = typeof tokenUsage.totalCompletionCost === 'number' ? tokenUsage.totalCompletionCost : undefined;
  const totalCost = typeof tokenUsage.totalCost === 'number'
    ? tokenUsage.totalCost
    : inputCost !== undefined && outputCost !== undefined
      ? inputCost + outputCost
      : undefined;

  if (inputCost !== undefined && outputCost !== undefined && totalCost !== undefined) {
    return { inputCost, outputCost, totalCost, currency };
  }

  const pricing = resolveModelPricing(modelKey, availableModels);
  if (!pricing) {
    return null;
  }

  return {
    inputCost: tokenUsage.totalPromptTokens * pricing.input,
    outputCost: tokenUsage.totalCompletionTokens * pricing.output,
    totalCost: (tokenUsage.totalPromptTokens * pricing.input) + (tokenUsage.totalCompletionTokens * pricing.output),
    currency
  };
}

export function formatModelDisplayName(modelKey?: string): string {
  if (!modelKey) {
    return 'Unknown Model';
  }

  const slug = (getCanonicalModelKey(modelKey) || modelKey).split('/').pop() || modelKey;
  return slug
    .split(/[-_]/)
    .map(part => {
      if (/^gpt$/i.test(part)) {
        return 'GPT';
      }

      if (/^\d+(\.\d+)?$/.test(part)) {
        return part;
      }

      return part.charAt(0).toUpperCase() + part.slice(1);
    })
    .join(' ');
}

/**
 * Calculates estimated cost based on token usage
 * Updated with correct OpenAI pricing per MILLION tokens including GPT-5 series
 */
export function calculateEstimatedCost(tokenUsage: ThreadTokenUsage, model: string = DEFAULT_MODEL_KEY): number {
  const costBreakdown = getThreadCostBreakdown(tokenUsage, model);

  if (!costBreakdown) {
    return 0;
  }

  return costBreakdown.totalCost;
}

/**
 * Formats token count for display (e.g., 1500 -> "1.5K", 1500000 -> "1.5M")
 */
export function formatTokenCount(count: number): string {
  if (count >= 1000000) {
    return (count / 1000000).toFixed(1) + 'M';
  } else if (count >= 1000) {
    return (count / 1000).toFixed(1) + 'K';
  }
  return count.toString();
}

